import { useState, useEffect } from 'react';

// Static feature flags for social trading
export const FEATURES = {
  // Existing features
  EDUCATIONAL_DEMO: true,
  AUTO_HEDGE: true,
  ENHANCED_WALKTHROUGH: true,
  
  // New social trading features
  SOCIAL_TRADING: true,
  TOURNAMENTS: true,
  COMPETITIONS: true,
  LEADERBOARDS: true,
  USER_PROFILES: true,
  
  // Feature rollback capability
  SOCIAL_REGISTRATION: true,
  COMPETITION_MODAL: true,
  TOURNAMENT_NOTIFICATIONS: true
};

// Dynamic feature flag hook for localStorage-based flags
export const useFeatureFlag = (flagName) => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    // Check if it's a static feature first
    if (FEATURES.hasOwnProperty(flagName)) {
      setEnabled(FEATURES[flagName]);
      return;
    }

    // Otherwise check localStorage for dynamic flags
    const flag = localStorage.getItem(`atticus_feature_${flagName}`) === 'true';
    setEnabled(flag);

    const handleStorageChange = (e) => {
      if (e.key === `atticus_feature_${flagName}`) {
        setEnabled(e.newValue === 'true');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [flagName]);

  return enabled;
};

// Set dynamic feature flags
export const setFeatureFlag = (flagName, enabled) => {
  localStorage.setItem(`atticus_feature_${flagName}`, enabled.toString());
  window.dispatchEvent(new StorageEvent('storage', {
    key: `atticus_feature_${flagName}`,
    newValue: enabled.toString()
  }));
};

// Get feature flag value (static or dynamic)
export const getFeatureFlag = (feature) => {
  // Check static features first
  if (FEATURES.hasOwnProperty(feature)) {
    return FEATURES[feature];
  }
  
  // Check localStorage for dynamic features
  if (typeof window !== 'undefined') {
    return localStorage.getItem(`atticus_feature_${feature}`) === 'true';
  }
  
  return false;
};

// Environment-based feature toggles
export const isFeatureEnabled = (feature) => {
  if (process.env.NODE_ENV === 'development') {
    return getFeatureFlag(feature);
  }
  return getFeatureFlag(feature);
};

// Social trading specific feature checks
export const isSocialTradingEnabled = () => getFeatureFlag('SOCIAL_TRADING');
export const areTournamentsEnabled = () => getFeatureFlag('TOURNAMENTS');
export const areCompetitionsEnabled = () => getFeatureFlag('COMPETITIONS');

// Console testing utilities for development
if (typeof window !== 'undefined') {
  // Existing demo utilities
  window.enableEnhancedDemo = () => setFeatureFlag('enhanced_walkthrough', true);
  window.disableEnhancedDemo = () => setFeatureFlag('enhanced_walkthrough', false);
  
  // New social trading utilities
  window.enableSocialTrading = () => setFeatureFlag('SOCIAL_TRADING', true);
  window.disableSocialTrading = () => setFeatureFlag('SOCIAL_TRADING', false);
  window.enableTournaments = () => setFeatureFlag('TOURNAMENTS', true);
  window.disableTournaments = () => setFeatureFlag('TOURNAMENTS', false);
  
  // Debug helper
  window.showFeatureFlags = () => {
    console.log('Current Feature Flags:', {
      static: FEATURES,
      dynamic: Object.keys(localStorage)
        .filter(key => key.startsWith('atticus_feature_'))
        .reduce((acc, key) => {
          const feature = key.replace('atticus_feature_', '');
          acc[feature] = localStorage.getItem(key) === 'true';
          return acc;
        }, {})
    });
  };
}

// Export default for backward compatibility
export default {
  useFeatureFlag,
  setFeatureFlag,
  getFeatureFlag,
  isFeatureEnabled,
  FEATURES
};
